import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Save, Clock, Truck, Phone, ChevronLeft, Store, Image as ImageIcon, Trash2, Camera, Receipt, AlertCircle } from 'lucide-react'
import { useStore } from '../../../contexts/StoreContext'
import { api } from '../../../lib/api'
import toast from 'react-hot-toast'
import { Store as StoreType } from '../../../types'
import ImageEditor from '../../../components/ImageEditor'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

interface StoreSettings {
  id: string
  name: string
  description: string
  address: string
  phone: string
  email: string
  delivery_fee: number
  minimum_order: number
  delivery_time: string
  is_open: boolean
  charges_itbis: boolean
  opening_hours: {
    monday: { open: string; close: string; closed: boolean }
    tuesday: { open: string; close: string; closed: boolean }
    wednesday: { open: string; close: string; closed: boolean }
    thursday: { open: string; close: string; closed: boolean }
    friday: { open: string; close: string; closed: boolean }
    saturday: { open: string; close: string; closed: boolean }
    sunday: { open: string; close: string; closed: boolean }
  }
}

const StoreSettings = () => {
  const navigate = useNavigate()
  const { activeStore, setActiveStore, refreshStores } = useStore()
  const [settings, setSettings] = useState<StoreSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadingRef, setLoadingRef] = useState(false) // Bandera para evitar múltiples cargas simultáneas
  
  // Estados para gestión de fotos
  const [logoSignedUrl, setLogoSignedUrl] = useState<string | null>(null)
  const [coverSignedUrl, setCoverSignedUrl] = useState<string | null>(null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)
  const [showImageEditor, setShowImageEditor] = useState(false)
  const [editingImageType, setEditingImageType] = useState<'logo' | 'cover' | null>(null)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    delivery_fee: '',
    minimum_order: '',
    delivery_time: '30-45 min',
    is_open: true,
    charges_itbis: true,
    opening_hours: {
      monday: { open: '08:00', close: '18:00', closed: false },
      tuesday: { open: '08:00', close: '18:00', closed: false },
      wednesday: { open: '08:00', close: '18:00', closed: false },
      thursday: { open: '08:00', close: '18:00', closed: false },
      friday: { open: '08:00', close: '18:00', closed: false },
      saturday: { open: '08:00', close: '18:00', closed: false },
      sunday: { open: '08:00', close: '18:00', closed: true }
    }
  })

  useEffect(() => {
    if (activeStore && !loadingRef) {
      loadStoreSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStore?.id]) // Solo cuando cambia el ID, no el objeto completo

  const loadStoreSettings = async () => {
    if (!activeStore || loadingRef) return // Evitar múltiples llamadas simultáneas

    const cacheKey = `store_settings_${activeStore.id}`
    
    // Intentar cargar desde cache primero
    const cachedSettings = getCacheStale<StoreSettings>(cacheKey)
    if (cachedSettings) {
      setSettings(cachedSettings)
      setLoading(false)
      setLoadingRef(false)
    }

    try {
      setLoadingRef(true) // Marcar que estamos cargando
      setLoading(true)
      // Obtener datos del negocio desde el endpoint my-stores/:storeId
      const response = await api.getMyStore(activeStore.id)
      
      if (response.success && response.data) {
        const storeData = response.data
        
        // Parsear operating_hours si viene como string JSON
        let operatingHours: any = {
          monday: { open: '08:00', close: '18:00', closed: false },
          tuesday: { open: '08:00', close: '18:00', closed: false },
          wednesday: { open: '08:00', close: '18:00', closed: false },
          thursday: { open: '08:00', close: '18:00', closed: false },
          friday: { open: '08:00', close: '18:00', closed: false },
          saturday: { open: '08:00', close: '18:00', closed: false },
          sunday: { open: '08:00', close: '18:00', closed: true }
        }
        
        if (storeData.operating_hours) {
          try {
            // Si es string, parsearlo
            if (typeof storeData.operating_hours === 'string') {
              operatingHours = JSON.parse(storeData.operating_hours)
            } else {
              operatingHours = storeData.operating_hours
            }
            
            // Validar y normalizar estructura
            const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            days.forEach(day => {
              if (!operatingHours[day]) {
                operatingHours[day] = { open: '08:00', close: '18:00', closed: day === 'sunday' }
              } else {
                // Asegurar que tenga todas las propiedades necesarias
                operatingHours[day] = {
                  open: operatingHours[day].open || '08:00',
                  close: operatingHours[day].close || '18:00',
                  closed: operatingHours[day].closed !== undefined ? operatingHours[day].closed : false
                }
              }
            })
          } catch (parseError) {
            console.error('Error parsing operating_hours:', parseError)
            // Usar valores por defecto si hay error al parsear
          }
        }

        // Convertir delivery_time de número a string para mostrar
        const formatDeliveryTime = (minutes: number | string | null | undefined): string => {
          if (!minutes) return '30-45 min'
          if (typeof minutes === 'string') return minutes
          // Si es número, convertir a formato string
          if (minutes <= 25) return '15-25 min'
          if (minutes <= 30) return '20-30 min'
          if (minutes <= 35) return '25-35 min'
          if (minutes <= 45) return '30-45 min'
          return '45-60 min'
        }

        const storeSettings: StoreSettings = {
          id: storeData.id,
          name: storeData.name || activeStore.name,
          description: storeData.description || '',
          address: storeData.address || activeStore.address || '',
          phone: storeData.phone || activeStore.phone || '',
          email: storeData.email || activeStore.email || '',
          delivery_fee: storeData.delivery_fee || 50,
          minimum_order: storeData.min_order_amount || 200,
          delivery_time: formatDeliveryTime(storeData.delivery_time),
          is_open: storeData.is_active !== false,
          charges_itbis: storeData.charges_itbis !== false, // Por defecto true
          opening_hours: operatingHours
        }

        setSettings(storeSettings)
        setFormData({
          name: storeSettings.name,
          description: storeSettings.description,
          address: storeSettings.address,
          phone: storeSettings.phone,
          email: storeSettings.email,
          delivery_fee: storeSettings.delivery_fee.toString(),
          minimum_order: storeSettings.minimum_order.toString(),
          delivery_time: storeSettings.delivery_time,
          is_open: storeSettings.is_open,
          charges_itbis: storeSettings.charges_itbis,
          opening_hours: storeSettings.opening_hours
        })
        
        // Cargar URLs firmadas de fotos
        if (storeData.logo_url) {
          await loadSignedUrl(storeData.logo_url, 'logo')
        } else {
          setLogoSignedUrl(null)
        }
        
        if (storeData.cover_image_url) {
          await loadSignedUrl(storeData.cover_image_url, 'cover')
        } else {
          setCoverSignedUrl(null)
        }
      } else {
        throw new Error('No se pudieron cargar los datos del negocio')
      }
    } catch (error: any) {
      console.error('Error loading store settings:', error)
      
      // Si es un error 404, redirigir inmediatamente sin reintentos
      if (error.response?.status === 404) {
        console.warn('Store not found (404), redirecting...')
        toast.error('El negocio no se encuentra. Redirigiendo a la lista de negocios...')
        setLoading(false)
        setLoadingRef(false)
        // Redirigir inmediatamente sin más intentos
        setTimeout(() => {
          navigate('/store-owner/my-stores')
        }, 500)
        return
      }
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedSettings) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar configuración')
      
      // Usar datos del activeStore como fallback solo si no es 404
      if (activeStore && error.response?.status !== 404) {
        setFormData({
          name: activeStore.name,
          description: '',
          address: activeStore.address || '',
          phone: activeStore.phone || '',
          email: activeStore.email || '',
          delivery_fee: '50',
          minimum_order: '200',
          delivery_time: '30-45 min',
          is_open: true,
          charges_itbis: true,
          opening_hours: {
            monday: { open: '08:00', close: '18:00', closed: false },
            tuesday: { open: '08:00', close: '18:00', closed: false },
            wednesday: { open: '08:00', close: '18:00', closed: false },
            thursday: { open: '08:00', close: '18:00', closed: false },
            friday: { open: '08:00', close: '18:00', closed: false },
            saturday: { open: '08:00', close: '18:00', closed: false },
            sunday: { open: '08:00', close: '18:00', closed: true }
          }
        })
      }
    } finally {
      setLoading(false)
      setLoadingRef(false) // Liberar la bandera
    }
  }

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!activeStore) return

    try {
      setSaving(true)

      // Validar horarios antes de enviar
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      for (const day of days) {
        const dayHours = formData.opening_hours[day as keyof typeof formData.opening_hours]
        if (!dayHours.closed) {
          if (dayHours.open >= dayHours.close) {
            toast.error(`La hora de cierre debe ser después de la hora de apertura para ${daysOfWeek.find(d => d.key === day)?.label}`)
            setSaving(false)
            return
          }
        }
      }

      // Convertir delivery_time de string "30-45 min" a número (promedio)
      const parseDeliveryTime = (timeStr: string): number => {
        // Extraer números del string "30-45 min" o "30 min"
        const match = timeStr.match(/(\d+)(?:-(\d+))?/)
        if (match) {
          const min = parseInt(match[1], 10)
          const max = match[2] ? parseInt(match[2], 10) : min
          // Retornar el promedio si hay rango, o el valor único
          return Math.round((min + max) / 2)
        }
        return 30 // Valor por defecto
      }

      // Actualizar negocio usando el endpoint my-store
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        delivery_fee: parseFloat(formData.delivery_fee) || 0,
        min_order_amount: parseFloat(formData.minimum_order) || 0,
        delivery_time: parseDeliveryTime(formData.delivery_time), // Convertir a número
        operating_hours: formData.opening_hours, // Enviar como objeto JSON
        is_active: formData.is_open,
        charges_itbis: formData.charges_itbis
      }

      const response = await api.updateMyStore(activeStore.id, updateData)

      if (response.success && response.data) {
        // Actualizar activeStore en el contexto con los nuevos datos
        const updatedStore: StoreType = {
          ...activeStore,
          name: response.data.name || activeStore.name,
          description: response.data.description || activeStore.description,
          address: response.data.address || activeStore.address,
          phone: response.data.phone || activeStore.phone,
          email: response.data.email || activeStore.email,
          deliveryFee: response.data.delivery_fee ?? activeStore.deliveryFee,
          minOrderAmount: response.data.min_order_amount ?? activeStore.minOrderAmount,
          deliveryTime: response.data.delivery_time || activeStore.deliveryTime,
          isActive: response.data.is_active !== undefined ? response.data.is_active : activeStore.isActive,
          operatingHours: response.data.operating_hours || activeStore.operatingHours,
          chargesItbis: response.data.charges_itbis !== undefined ? response.data.charges_itbis : (activeStore.chargesItbis !== false)
        }
        
        setActiveStore(updatedStore)
        
        toast.success('Configuración guardada exitosamente')
        
        // Refrescar stores en el contexto primero
        if (refreshStores) {
          await refreshStores()
        }
        
        // Esperar un momento antes de recargar para asegurar que el store esté actualizado
        setTimeout(async () => {
          try {
            await loadStoreSettings()
          } catch (error) {
            // Si falla la recarga, no es crítico ya que tenemos los datos actualizados
            console.warn('No se pudo recargar la configuración, pero los cambios se guardaron correctamente')
          }
        }, 500)
      } else {
        throw new Error(response.message || 'Error al guardar')
      }
    } catch (error: any) {
      console.error('Error saving store settings:', error)
      toast.error(error.response?.data?.message || 'Error al guardar configuración')
    } finally {
      setSaving(false)
    }
  }

  const updateOpeningHour = (day: keyof typeof formData.opening_hours, field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: {
          ...prev.opening_hours[day],
          [field]: value
        }
      }
    }))
  }

  // Funciones para gestión de fotos
  const loadSignedUrl = async (key: string, type: 'logo' | 'cover') => {
    try {
      const response = await api.get(`/upload/signed-url/${encodeURIComponent(key)}`)
      if (response.data.success && response.data.data?.signedUrl) {
        if (type === 'logo') {
          setLogoSignedUrl(response.data.data.signedUrl)
        } else {
          setCoverSignedUrl(response.data.data.signedUrl)
        }
      }
    } catch (error) {
      console.error(`Error loading ${type} signed URL:`, error)
    }
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'cover') => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen')
      return
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB')
      return
    }

    setEditingImageType(type)
    setSelectedImageFile(file)
    setShowImageEditor(true)
    
    // Limpiar el input
    event.target.value = ''
  }

  const handleSaveEditedImage = async (editedFile: File) => {
    if (!activeStore || !editingImageType) return

    setShowImageEditor(false)
    
    if (editingImageType === 'logo') {
      setUploadingLogo(true)
    } else {
      setUploadingCover(true)
    }
    
    try {
      // Subir imagen editada
      const formData = new FormData()
      formData.append('image', editedFile)
      formData.append('folder', 'stores')

      const uploadResponse = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (uploadResponse.data.success && uploadResponse.data.data?.key) {
        // Actualizar store con la nueva foto
        if (editingImageType === 'logo') {
          await api.updateStoreLogo(activeStore.id, uploadResponse.data.data.key)
          await loadSignedUrl(uploadResponse.data.data.key, 'logo')
          toast.success('Logo actualizado exitosamente')
        } else {
          await api.updateStoreCover(activeStore.id, uploadResponse.data.data.key)
          await loadSignedUrl(uploadResponse.data.data.key, 'cover')
          toast.success('Imagen de portada actualizada exitosamente')
        }
        
        setSelectedImageFile(null)
        setEditingImageType(null)
        
        // Recargar store settings
        await loadStoreSettings()
        await refreshStores()
      } else {
        throw new Error('Error al subir la imagen')
      }
    } catch (error: any) {
      console.error('Error uploading image:', error)
      toast.error(error.response?.data?.message || `Error al subir la ${editingImageType === 'logo' ? 'logo' : 'imagen de portada'}`)
      setSelectedImageFile(null)
      setEditingImageType(null)
    } finally {
      setUploadingLogo(false)
      setUploadingCover(false)
    }
  }

  const handleCancelImageEdit = () => {
    setShowImageEditor(false)
    setSelectedImageFile(null)
    setEditingImageType(null)
  }

  const handleDeleteLogo = async () => {
    if (!activeStore) return
    
    if (!confirm('¿Estás seguro de que quieres eliminar el logo? Esta acción eliminará el logo permanentemente.')) {
      return
    }

    setUploadingLogo(true)
    try {
      await api.deleteStoreLogo(activeStore.id)
      toast.success('Logo eliminado exitosamente')
      setLogoSignedUrl(null)
      await loadStoreSettings()
      await refreshStores()
    } catch (error: any) {
      console.error('Error deleting logo:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar el logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleDeleteCover = async () => {
    if (!activeStore) return
    
    if (!confirm('¿Estás seguro de que quieres eliminar la imagen de portada? Esta acción eliminará la imagen permanentemente.')) {
      return
    }

    setUploadingCover(true)
    try {
      await api.deleteStoreCover(activeStore.id)
      toast.success('Imagen de portada eliminada exitosamente')
      setCoverSignedUrl(null)
      await loadStoreSettings()
      await refreshStores()
    } catch (error: any) {
      console.error('Error deleting cover:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar la imagen de portada')
    } finally {
      setUploadingCover(false)
    }
  }

  const daysOfWeek = [
    { key: 'monday', label: 'Lunes' },
    { key: 'tuesday', label: 'Martes' },
    { key: 'wednesday', label: 'Miércoles' },
    { key: 'thursday', label: 'Jueves' },
    { key: 'friday', label: 'Viernes' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' }
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando configuración...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism Style - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 sm:py-8">
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-3 rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="flex-1 text-center px-4">
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                  Configuración del Negocio
                </h1>
                <p className="text-sm text-gray-600 font-semibold truncate">
                  {settings?.name}
                </p>
              </div>
              <div className="w-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <form onSubmit={handleSaveSettings} className="space-y-8">
          {/* Store Photos */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl flex items-center justify-center mr-4">
                <ImageIcon className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Fotos del Negocio</h2>
                <p className="text-sm text-gray-500 mt-1">Personaliza la imagen de tu negocio</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Logo */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Logo del Negocio
                </label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center flex-shrink-0">
                    {logoSignedUrl ? (
                      <img src={logoSignedUrl} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <Store className="w-10 h-10 text-gray-400" />
                    )}
                    {uploadingLogo && (
                      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-3 min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e, 'logo')}
                        className="hidden"
                        id="logo-upload"
                        disabled={uploadingLogo}
                      />
                      <label
                        htmlFor="logo-upload"
                        className={`inline-flex items-center px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 cursor-pointer transition-all duration-200 text-sm font-medium active:scale-95 shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30 ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        {logoSignedUrl ? 'Cambiar Logo' : 'Subir Logo'}
                      </label>
                      {logoSignedUrl && (
                        <button
                          type="button"
                          onClick={handleDeleteLogo}
                          disabled={uploadingLogo}
                          className={`inline-flex items-center px-4 py-2.5 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-all duration-200 text-sm font-medium active:scale-95 border border-red-200 ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 font-medium">
                      Recomendado: Imagen cuadrada, mínimo 200x200px, máximo 5MB
                    </p>
                  </div>
                </div>
              </div>

              {/* Cover Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Imagen de Portada
                </label>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="relative w-full sm:w-40 h-24 sm:h-28 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center flex-shrink-0">
                    {coverSignedUrl ? (
                      <img src={coverSignedUrl} alt="Portada" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="w-10 h-10 text-gray-400" />
                    )}
                    {uploadingCover && (
                      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-3 min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageSelect(e, 'cover')}
                        className="hidden"
                        id="cover-upload"
                        disabled={uploadingCover}
                      />
                      <label
                        htmlFor="cover-upload"
                        className={`inline-flex items-center px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 cursor-pointer transition-all duration-200 text-sm font-medium active:scale-95 shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30 ${uploadingCover ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        {coverSignedUrl ? 'Cambiar Portada' : 'Subir Portada'}
                      </label>
                      {coverSignedUrl && (
                        <button
                          type="button"
                          onClick={handleDeleteCover}
                          disabled={uploadingCover}
                          className={`inline-flex items-center px-4 py-2.5 bg-red-50 text-red-700 rounded-xl hover:bg-red-100 transition-all duration-200 text-sm font-medium active:scale-95 border border-red-200 ${uploadingCover ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 font-medium">
                      Recomendado: Imagen horizontal 16:9, mínimo 1200x675px, máximo 5MB
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl flex items-center justify-center mr-4">
                <Store className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Información Básica</h2>
                <p className="text-sm text-gray-500 mt-1">Datos principales de tu negocio</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Nombre del Negocio
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md resize-none"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl flex items-center justify-center mr-4">
                <Phone className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Información de Contacto</h2>
                <p className="text-sm text-gray-500 mt-1">Datos de contacto de tu negocio</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Dirección
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Settings */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl flex items-center justify-center mr-4">
                <Truck className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Configuración de Delivery</h2>
                <p className="text-sm text-gray-500 mt-1">Ajusta los parámetros de entrega</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Tarifa de Delivery (RD$)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">RD$</span>
                  <input
                    type="number"
                    value={formData.delivery_fee}
                    onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                    className="w-full pl-14 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Pedido Mínimo (RD$)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">RD$</span>
                  <input
                    type="number"
                    value={formData.minimum_order}
                    onChange={(e) => setFormData({ ...formData, minimum_order: e.target.value })}
                    className="w-full pl-14 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5">
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Tiempo Estimado de Delivery
              </label>
              <select
                value={formData.delivery_time}
                onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
              >
                <option value="15-25 min">15-25 minutos</option>
                <option value="20-30 min">20-30 minutos</option>
                <option value="25-35 min">25-35 minutos</option>
                <option value="30-45 min">30-45 minutos</option>
                <option value="45-60 min">45-60 minutos</option>
              </select>
            </div>
          </div>

          {/* Opening Hours */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl flex items-center justify-center mr-4">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Horarios de Atención</h2>
                <p className="text-sm text-gray-500 mt-1">Configura los horarios de atención para cada día de la semana</p>
              </div>
            </div>

            <div className="space-y-3">
              {daysOfWeek.map(({ key, label }) => {
                const dayHours = formData.opening_hours[key as keyof typeof formData.opening_hours]
                const isClosed = dayHours.closed
                
                return (
                  <div 
                    key={key} 
                    className={`p-4 sm:p-5 rounded-2xl border-2 transition-all duration-200 ${
                      isClosed 
                        ? 'bg-gray-50 border-gray-200' 
                        : 'bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                      <div className="flex items-center">
                        <span className="text-base sm:text-lg font-semibold text-gray-900 min-w-[100px]">
                          {label}
                        </span>
                      </div>
                      
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!isClosed}
                          onChange={(e) => updateOpeningHour(key as keyof typeof formData.opening_hours, 'closed', !e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        <span className="ml-3 text-sm font-semibold text-gray-700">
                          {isClosed ? 'Cerrado' : 'Abierto'}
                        </span>
                      </label>
                    </div>

                    {!isClosed && (
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-600 mb-2">
                            Apertura
                          </label>
                          <input
                            type="time"
                            value={dayHours.open}
                            onChange={(e) => updateOpeningHour(key as keyof typeof formData.opening_hours, 'open', e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm shadow-sm hover:shadow-md"
                          />
                        </div>
                        
                        <div className="flex items-center justify-center pt-6 sm:pt-0">
                          <span className="text-gray-500 font-bold text-lg">-</span>
                        </div>
                        
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-gray-600 mb-2">
                            Cierre
                          </label>
                          <input
                            type="time"
                            value={dayHours.close}
                            onChange={(e) => {
                              const closeTime = e.target.value
                              const openTime = dayHours.open
                              
                              // Validar que la hora de cierre sea después de la de apertura
                              if (closeTime <= openTime) {
                                toast.error('La hora de cierre debe ser después de la hora de apertura')
                                return
                              }
                              
                              updateOpeningHour(key as keyof typeof formData.opening_hours, 'close', closeTime)
                            }}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm shadow-sm hover:shadow-md"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const defaultHours = {
                      monday: { open: '08:00', close: '18:00', closed: false },
                      tuesday: { open: '08:00', close: '18:00', closed: false },
                      wednesday: { open: '08:00', close: '18:00', closed: false },
                      thursday: { open: '08:00', close: '18:00', closed: false },
                      friday: { open: '08:00', close: '18:00', closed: false },
                      saturday: { open: '08:00', close: '18:00', closed: false },
                      sunday: { open: '08:00', close: '18:00', closed: true }
                    }
                    setFormData(prev => ({ ...prev, opening_hours: defaultHours }))
                    toast.success('Horarios restablecidos a valores por defecto')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200 active:scale-95 border border-gray-200"
                >
                  Restablecer por defecto
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const allOpen = {
                      monday: { open: '08:00', close: '20:00', closed: false },
                      tuesday: { open: '08:00', close: '20:00', closed: false },
                      wednesday: { open: '08:00', close: '20:00', closed: false },
                      thursday: { open: '08:00', close: '20:00', closed: false },
                      friday: { open: '08:00', close: '20:00', closed: false },
                      saturday: { open: '08:00', close: '20:00', closed: false },
                      sunday: { open: '08:00', close: '20:00', closed: false }
                    }
                    setFormData(prev => ({ ...prev, opening_hours: allOpen }))
                    toast.success('Todos los días configurados como abiertos')
                  }}
                  className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-xl hover:bg-primary-100 transition-all duration-200 active:scale-95 border border-primary-200"
                >
                  Abrir todos los días
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const allClosed = {
                      monday: { open: '08:00', close: '18:00', closed: true },
                      tuesday: { open: '08:00', close: '18:00', closed: true },
                      wednesday: { open: '08:00', close: '18:00', closed: true },
                      thursday: { open: '08:00', close: '18:00', closed: true },
                      friday: { open: '08:00', close: '18:00', closed: true },
                      saturday: { open: '08:00', close: '18:00', closed: true },
                      sunday: { open: '08:00', close: '18:00', closed: true }
                    }
                    setFormData(prev => ({ ...prev, opening_hours: allClosed }))
                    toast.success('Todos los días configurados como cerrados')
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-xl hover:bg-red-100 transition-all duration-200 active:scale-95 border border-red-200"
                >
                  Cerrar todos los días
                </button>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8 space-y-6">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl border border-green-200/50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Store className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Estado del Negocio</h3>
                    <p className="text-sm text-gray-600 mt-1">¿El negocio está abierto para recibir pedidos?</p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={formData.is_open}
                    onChange={(e) => setFormData({ ...formData, is_open: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>

              {!formData.is_open && (
                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900 mb-1">Negocio Inactivo</p>
                      <p className="text-xs text-amber-700">
                        Tu negocio no aparecerá en las búsquedas de los clientes mientras esté inactivo. 
                        Los clientes no podrán realizar pedidos hasta que lo actives nuevamente.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Charges ITBIS */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl border border-blue-200/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Receipt className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Cobrar ITBIS</h3>
                  <p className="text-sm text-gray-600 mt-1">¿El negocio cobra ITBIS (18%) en los productos?</p>
                </div>
              </div>

              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                <input
                  type="checkbox"
                  checked={formData.charges_itbis}
                  onChange={(e) => setFormData({ ...formData, charges_itbis: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8">
            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center px-8 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg shadow-xl hover:shadow-2xl"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Guardar Configuración
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Image Editor Modal */}
      {showImageEditor && selectedImageFile && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white/80 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl max-w-3xl w-full my-8 border border-white/30">
            <ImageEditor
              image={selectedImageFile}
              onSave={handleSaveEditedImage}
              onCancel={handleCancelImageEdit}
              aspectRatio={editingImageType === 'logo' ? 1 : 16/9}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default StoreSettings
